import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Database,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ShieldOff,
  RotateCcw,
  PlugZap,
  Save,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import Proxy911IpUsagePanel from '../../components/admin/data-providers/Proxy911IpUsagePanel';
import { toast } from 'sonner';

const TABS = [
  { id: 'configuration', label: 'Configuration' },
  { id: 'ip-usage', label: 'IP Usage' },
];

function safeErrorMessage(error) {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Unable to update the DataImpulse provider.';
  const lowered = message.toLowerCase();
  if (lowered.includes('traceback') || lowered.includes('password') || lowered.includes('secret')) {
    return 'Unable to update the DataImpulse provider.';
  }
  return message;
}

const EMPTY_FORM = {
  status: 'disabled',
  protocol: 'http',
  host: 'gw.dataimpulse.com',
  port: '823',
  providerAccountRef: '',
  username: '',
  password: '',
};

function formFromProvider(provider) {
  return {
    status: provider?.status || 'disabled',
    protocol: provider?.protocol === 'https' ? 'http' : (provider?.protocol || 'http'),
    host: provider?.host || 'gw.dataimpulse.com',
    port: provider?.port != null ? String(provider.port) : '823',
    providerAccountRef: provider?.providerAccountRef || provider?.provider_account_ref || '',
    username: '',
    password: '',
  };
}

const DataImpulseProviderPage = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('configuration');
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [rotateUsername, setRotateUsername] = useState('');
  const [rotatePassword, setRotatePassword] = useState('');
  const [showRotate, setShowRotate] = useState(false);

  const credentialsConfigured = Boolean(
    provider?.credentialsConfigured ?? provider?.credentials_configured,
  );

  const loadProvider = async () => {
    setLoading(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.getDataimpulseProvider();
      setProvider(payload);
      setForm(formFromProvider(payload));
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProvider();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setConfirmDisable(false);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const body = {
        status: form.status,
        protocol: form.protocol === 'https' ? 'http' : form.protocol,
        host: form.host.trim() || 'gw.dataimpulse.com',
        port: Number(form.port) || 823,
        provider_account_ref: form.providerAccountRef.trim() || null,
      };
      if (form.username.trim()) body.username = form.username.trim();
      if (form.password) body.password = form.password;
      const payload = await adminOpsService.putDataimpulseProvider(body);
      setProvider(payload);
      setForm(formFromProvider(payload));
      toast.success('Provider configuration saved');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const result = await adminOpsService.testDataimpulseProvider();
      const ok = result?.ok !== false && result?.success !== false;
      toast.success(ok ? 'Provider connection succeeded' : (result?.message || 'Connection test finished'));
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    if (!rotateUsername.trim() || !rotatePassword) {
      setError('Enter both username and password to rotate credentials.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.rotateDataimpulseProvider({
        username: rotateUsername.trim(),
        password: rotatePassword,
      });
      setProvider(payload);
      setForm(formFromProvider(payload));
      setRotateUsername('');
      setRotatePassword('');
      setShowRotate(false);
      toast.success('Credentials rotated');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.disableDataimpulseProvider();
      setProvider(payload);
      setForm(formFromProvider(payload));
      setConfirmDisable(false);
      toast.success('Provider disabled');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = String(provider?.status || 'disabled');
  const statusColor = statusLabel === 'active'
    ? 'text-emerald-400'
    : statusLabel === 'suspended'
      ? 'text-amber-400'
      : 'text-slate-400';

  return (
    <div className="space-y-6" data-testid="dataimpulse-provider-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Database className="text-amber-400" size={28} />
            DataImpulse Provider
          </h1>
          <p className="text-slate-500 mt-1">
            Global gateway credentials for Authorized QA Proxy. Secrets are never shown after save.
          </p>
        </div>
        {activeTab === 'configuration' ? (
          <Button variant="outline" onClick={loadProvider} disabled={loading || busy}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        ) : null}
      </div>

      <div className="flex gap-1 border-b border-stone-800" role="tablist" data-testid="dataimpulse-provider-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              activeTab === tab.id
                ? 'border-amber-400 text-white'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`dataimpulse-provider-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ip-usage' ? <Proxy911IpUsagePanel provider="dataimpulse" /> : null}

      {activeTab === 'configuration' ? (
      <>
      {error ? (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4"
          data-testid="dataimpulse-provider-error"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, index) => <Skeleton key={index} className="h-28 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Status</p>
            <p className={`text-3xl font-bold capitalize ${statusColor}`} data-testid="dataimpulse-provider-status">
              {statusLabel}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Credentials</p>
            <p
              className={`text-lg font-semibold ${credentialsConfigured ? 'text-emerald-400' : 'text-amber-400'}`}
              data-testid="dataimpulse-provider-credentials"
            >
              {credentialsConfigured ? 'Configured securely' : 'Not configured'}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Connection</p>
            <p className="text-lg font-semibold text-white" data-testid="dataimpulse-provider-connection">
              {provider?.host || 'gw.dataimpulse.com'}:{provider?.port ?? 823}
            </p>
          </div>
        </div>
      )}

      <form
        className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4"
        onSubmit={handleSave}
        data-testid="dataimpulse-provider-form"
      >
        <div className="flex items-center gap-3">
          {credentialsConfigured ? (
            <CheckCircle className="text-emerald-400" size={20} />
          ) : (
            <AlertTriangle className="text-amber-400" size={20} />
          )}
          <p className="text-sm text-slate-400">
            {credentialsConfigured
              ? 'Provider credentials are stored encrypted. Leave username/password blank to keep the saved values.'
              : 'Configure host, port, and credentials before enabling QA proxy sessions.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="space-y-1">
            <span className="text-sm text-slate-400">Status</span>
            <select
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              value={form.status}
              onChange={handleChange('status')}
              data-testid="dataimpulse-provider-status-select"
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Protocol</span>
            <select
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              value={form.protocol}
              onChange={handleChange('protocol')}
              data-testid="dataimpulse-provider-protocol"
            >
              <option value="http">HTTP/HTTPS</option>
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Host</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              value={form.host}
              onChange={handleChange('host')}
              autoComplete="off"
              data-testid="dataimpulse-provider-host"
            />
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Port</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              type="number"
              min="1"
              max="65535"
              value={form.port}
              onChange={handleChange('port')}
              data-testid="dataimpulse-provider-port"
            />
          </label>

          <label className="space-y-1 md:col-span-2">
            <span className="text-sm text-slate-400">Provider account / reference (optional)</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              value={form.providerAccountRef}
              onChange={handleChange('providerAccountRef')}
              autoComplete="off"
              data-testid="dataimpulse-provider-account-ref"
            />
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Username</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              value={form.username}
              onChange={handleChange('username')}
              autoComplete="off"
              placeholder={credentialsConfigured ? 'Saved securely.' : ''}
              data-testid="dataimpulse-provider-username"
            />
            {credentialsConfigured ? (
              <span className="text-xs text-slate-500" data-testid="dataimpulse-provider-username-hint">
                Saved securely.
              </span>
            ) : null}
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Password</span>
            <input
              className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
              type="password"
              value={form.password}
              onChange={handleChange('password')}
              autoComplete="new-password"
              placeholder="Saved securely. Enter only to replace."
              data-testid="dataimpulse-provider-password"
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="outline" onClick={handleTest} disabled={busy || loading} data-testid="dataimpulse-provider-test">
            <PlugZap size={16} className="mr-2" />
            Test provider connection
          </Button>
          <Button type="submit" disabled={busy || loading} data-testid="dataimpulse-provider-save">
            <Save size={16} className="mr-2" />
            {busy ? 'Saving…' : 'Save provider configuration'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowRotate((value) => !value)}
            disabled={busy || loading}
            data-testid="dataimpulse-provider-rotate-toggle"
          >
            <RotateCcw size={16} className="mr-2" />
            Rotate credentials
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmDisable(true)}
            disabled={busy || loading}
            data-testid="dataimpulse-provider-disable"
          >
            <ShieldOff size={16} className="mr-2" />
            Disable provider
          </Button>
        </div>

        {showRotate ? (
          <div
            className="bg-stone-950 border border-stone-800 rounded-xl p-4 space-y-3"
            data-testid="dataimpulse-provider-rotate-form"
          >
            <p className="text-sm text-slate-400">Enter newly rotated credentials. Previous secrets are never displayed.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
                value={rotateUsername}
                onChange={(event) => setRotateUsername(event.target.value)}
                placeholder="New username"
                autoComplete="off"
                data-testid="dataimpulse-provider-rotate-username"
              />
              <input
                className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
                type="password"
                value={rotatePassword}
                onChange={(event) => setRotatePassword(event.target.value)}
                placeholder="New password"
                autoComplete="new-password"
                data-testid="dataimpulse-provider-rotate-password"
              />
            </div>
            <Button type="button" onClick={handleRotate} disabled={busy} data-testid="dataimpulse-provider-rotate-save">
              Save rotated credentials
            </Button>
          </div>
        ) : null}

        {confirmDisable ? (
          <div
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3"
            data-testid="dataimpulse-provider-disable-confirm"
          >
            <p className="text-sm text-red-200">
              Disable the global DataImpulse provider? QA proxy sessions will stop routing until it is re-enabled.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="destructive"
                onClick={handleDisable}
                disabled={busy}
                data-testid="dataimpulse-provider-disable-confirm-button"
              >
                Disable provider
              </Button>
              <Button variant="outline" onClick={() => setConfirmDisable(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </form>
      </>
      ) : null}
    </div>
  );
};

export default DataImpulseProviderPage;
